// Types pour l'application Admin Cloud

export interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  organization_id?: string | null;
  organization?: Organization | null;
  role: UserRole;
  email_verified?: boolean;
  created_at?: string;
}

/**
 * Rôles applicatifs — miroir de la contrainte `users_role_check` en base.
 *
 * Une seule source pour les deux sélecteurs de la page Admin : c'est leur
 * duplication qui avait laissé survivre un rôle `user` mort dans l'un et
 * l'autre, longtemps après que le reste du produit l'ait oublié.
 */
export type UserRole = 'admin' | 'organizer' | 'participant';

/** Du moins privilégié au plus privilégié — l'ordre d'affichage. */
export const USER_ROLES: readonly UserRole[] = ['participant', 'organizer', 'admin'];

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  participant: 'Participant',
  organizer: 'Organizer',
  admin: 'Admin',
};

export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  created_at: string;
}

export interface Event {
  id: string;
  name: string;
  slug?: string;
  event_type: 'course' | 'crossfit';
  date: string; // @deprecated - Utiliser date_start/date_end
  date_start: string;
  date_end: string;
  description?: string;
  location?: string;
  location_street?: string;
  location_postal_code?: string;
  location_city?: string;
  location_country?: string;
  venue_name?: string; // Nom de la box/salle (ex: "CrossFit Ambiorix") — SEO + affichage public
  status: 'draft' | 'published' | 'active' | 'completed' | 'archived';
  visibility?: 'public' | 'private' | 'unlisted'; // Visibilité de l'événement
  logo_url?: string | null; // Logo propre à l'événement (LOG-90, fallback sur logo orga)
  contest_enabled?: boolean; // Admin LogFit : section concours dans les emails d'inscription (opt-in par event)
  max_participants?: number;
  // LOG-104 : comptés à la volée par GET /events, jamais stockés. `capacity`
  // vaut null quand au moins une catégorie d'inscription n'a pas de plafond.
  registrations_count?: number;
  registrations_capacity?: number | null;
  event_code: string;
  organization_id: string;
  org_slug?: string; // Slug de l'organisation (pour URLs publiques)
  lanes_count?: number; // Pour CrossFit
  created_at: string;
  updated_at: string;
}

export interface Team {
  id: string;
  team_id: string; // Format 0001, 0002, etc.
  name: string;
  participants?: string;
  contact_email?: string;
  contact_phone?: string;
  category_id?: string;
  event_id: string;
  created_at: string;
  // null quand l'equipe ne vient pas d'une inscription (import Excel, saisie
  // manuelle) : il n'y a alors ni paiement ni remboursement possible.
  registration?: {
    id: string;
    status: string;
    amount_total_cents: number | null;
    // Part de l'organisateur : ce que l'athlete recupere par defaut, les frais
    // de service restant acquis.
    amount_organizer_cents: number | null;
    // Frais de service, que l'organisateur peut choisir de prendre a sa charge.
    amount_fee_cents: number | null;
  } | null;
}

export interface Category {
  id: string;
  name: string;
  display_name?: string;
  code?: string | null;
  description?: string;
  gender?: 'M' | 'F' | 'MIXED';
  min_age?: number;
  max_age?: number;
  /** Ordre posé par « Réorganiser » (null = jamais ordonnée, en fin de liste). */
  display_order?: number | null;
  event_id: string;
  event_name?: string;
}

export interface CategoryGroup {
  id: string;
  name: string;
  description?: string;
  category_ids: string[];
  category_names?: string[]; // Noms des catégories pour l'affichage
  is_active: boolean;
  event_id: string;
  created_at?: string;
  updated_at?: string;
}

export interface CategoryGroupForm {
  name: string;
  description?: string;
  category_ids: string[];
  is_active: boolean;
}

export interface WodPart {
  part_number: number;
  name: string;
  type: 'for_time' | 'amrap' | 'max_load' | 'rest';
  time_cap_minutes?: number;
  duration_seconds?: number; // Pour les pauses (type='rest')
  description?: string;
  max_points?: number; // Points attribués pour cette partie
  scoring_type?: string; // 'time', 'reps', 'weight'
  is_mandatory?: boolean; // Pour les pauses
  structure?: any; // Structure hiérarchique spécifique à cette partie (WodStructure)
  // 🆕 Configuration Max Load (2025-12-28)
  max_load_reps?: number;          // Nombre de tentatives (ex: 3 pour "3 Clean and Jerk")
  max_load_scoring_mode?: 'max' | 'total'; // 'max' = meilleur poids, 'total' = somme des poids réussis
  team_total?: boolean; // charge comptée dans le total de l'équipe (classement sur la somme des parties)
}

export interface WOD {
  id: string;
  event_id: string;
  wod_number: number;
  name: string;
  type: 'FOR TIME' | 'AMRAP' | 'EMOM' | 'TABATA' | 'MAX LOAD';
  time_cap?: number; // En minutes
  description?: string;
  scoring_type: 'time' | 'reps' | 'weight';
  structure?: any; // Structure hiérarchique du WOD (WodStructure)
  movements?: string[]; // Liste plate des mouvements (pour compatibilité)
  // 🆕 Support WODs Multi-Parties
  parts?: WodPart[]; // Liste des parties (null = WOD simple)
  status?: 'active' | 'inactive'; // Statut du WOD (défaut: active)
  // Paliers de divulgation : Brouillon → Mystère → Annoncé → Public. Le palier n'est pas
  // stocké, il se déduit de ces deux dates — le serveur le renvoie calculé.
  palier?: 'draft' | 'mystery' | 'announced' | 'public';
  mystery_at?: string | null; // ISO8601 UTC ; null = pas de bascule programmée
  announced_at?: string | null;
  public_at?: string | null;
  is_mystery?: boolean; // dérivé : palier === 'mystery'. Lu par des écrans publics.
  // WOD éliminatoire (LOG-219) : accès réservé aux équipes ayant TERMINÉ ce WOD
  requires_finished_wod_id?: string | null;
  requires_finished_wod_name?: string | null;
  /** Équipes qualifiées (ont terminé le prérequis), recalculées à chaque lecture ; null sans prérequis. */
  qualified_team_ids?: string[] | null;
  /** Ordre d'affichage des variantes de catégorie (clés de `parts`). */
  variant_order?: string[] | null;
  created_at?: string;
  updated_at?: string;
}

export interface Result {
  id: string;
  team_id: string;
  event_id: string;
  time_value: number; // En secondes
  rank?: number;
  status: 'finished' | 'dnf' | 'dns' | 'dq';
  created_at: string;
}

export interface SyncStatus {
  event_code: string;
  local_count: number;
  cloud_count: number;
  last_sync: string;
  pending_uploads: number;
  status: 'synced' | 'pending' | 'error';
}

// Types pour les formulaires
export interface LoginForm {
  email: string;
  password: string;
}

export interface EventForm {
  name: string;
  event_type: 'course' | 'crossfit';
  date_start: string;
  date_end: string;
  description: string;
  location: string;
  location_street: string;
  location_postal_code: string;
  location_city: string;
  location_country: string;
  venue_name: string;
  status: Event['status'];
  visibility?: 'public' | 'private' | 'unlisted';
  max_participants: number;
  lanes_count?: number;
}

/**
 * Config de l'écran Roxzone Display (sponsors + carousel) — table
 * `roxzone_display_config`, une ligne par événement.
 */
/** Un logo sponsor déposé par l'organisateur (l'écran compose la grille). */
export interface SponsorLogo {
  id: string;
  url: string;
  display_order: number;
}

export interface DisplayConfig {
  id: string;
  event_id: string;
  sponsor_enabled: boolean;
  sponsor_logos: SponsorLogo[];
  sponsor_frequency: number;
  /** Durée d'affichage d'une page — écran TV et overlay OBS. */
  carousel_seconds: number;
  /** Overlay OBS : secondes de vidéo seule entre deux apparitions (0 = continu). */
  overlay_gap_seconds: number;
}

export interface TeamForm {
  name: string;
  participants: string;
  contact_email: string;
  contact_phone: string;
  category_id: string;
  team_id?: string; // ID personnalisé optionnel (sinon auto-généré)
}

export interface CategoryForm {
  name: string;
  display_name?: string;
  code: string;
  description: string;
  gender?: Category['gender'];
  min_age?: number;
  max_age?: number;
}

export interface WODForm {
  wod_number: number;
  requires_finished_wod_id?: string | null; // WOD éliminatoire (LOG-219)
  name: string;
  type: WOD['type'];
  time_cap?: number;
  description: string;
  scoring_type: WOD['scoring_type'];
  structure?: any; // Structure hiérarchique du WOD
  movements?: string[]; // Liste plate des mouvements
  // 🆕 Support WODs Multi-Parties
  parts?: WodPart[]; // Liste des parties (null = WOD simple)
  status?: 'active' | 'inactive';
  is_mystery?: boolean;
  reveal_at?: string | null;
}